import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ChannelSnapshotStatus } from '@playerbase/types';

import { useShell } from '../shell/use-shell.js';
import { ChannelSnapshotCard } from './channel-snapshot-card.js';
import { fetchChannelSnapshots } from './insights-data.js';

// Channel-status sub-page at /s/:studioShortId/g/:gameShortId/insights/channels.
// Power-user / debugging surface that hosts the per-channel snapshot cards
// (with their individual Refresh buttons and per-channel cooldowns). Now
// orphaned from the main /insights page after the headline rework removed
// the status strip - reachable by URL only until we wire it back in from a
// dedicated entry point.

export function InsightsChannelsPage(): JSX.Element {
  const { activeStudio, activeGame, insightsVersion } = useShell();
  const gameId = activeGame?.id ?? null;

  const [channels, setChannels] = useState<ChannelSnapshotStatus[]>([]);
  const [loaded, setLoaded] = useState(false);

  // The shell owns the single insights:<gameId> subscription; the page just
  // refetches when its version ticks.
  const version = insightsVersion;

  const refresh = useCallback(async () => {
    if (gameId === null) return;
    const c = await fetchChannelSnapshots(gameId);
    setChannels(c);
    setLoaded(true);
  }, [gameId]);

  useEffect(() => {
    if (gameId === null) {
      setChannels([]);
      setLoaded(false);
      return;
    }
    setLoaded(false);
    void refresh();
  }, [gameId, refresh]);

  // Debounce-coalesce realtime bursts into one refetch.
  useEffect(() => {
    if (gameId === null || version === 0) return;
    const handle = setTimeout(() => void refresh(), 500);
    return () => clearTimeout(handle);
  }, [gameId, version, refresh]);

  const insightsHref = useMemo(() => {
    if (activeGame === null) return '';
    return `/s/${activeStudio.shortId}/g/${activeGame.shortId}/insights`;
  }, [activeStudio.shortId, activeGame]);

  const watchedChannelsHref = useMemo(() => {
    if (activeGame === null) return '';
    return `/s/${activeStudio.shortId}/g/${activeGame.shortId}/feedback-settings/watched-channels`;
  }, [activeStudio.shortId, activeGame]);

  if (activeGame === null) {
    return (
      <div className="mx-auto w-full max-w-4xl p-6">
        <h1 className="text-xl font-semibold text-text">Channel status</h1>
        <p className="mt-2 text-sm text-text-muted">Pick a game to view channel status.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <Link to={insightsHref} className="text-sm text-link underline">
        ← Back to insights
      </Link>

      <header>
        <h1 className="text-xl font-semibold text-text">Channel status</h1>
        <p className="mt-1 text-sm text-text-muted">
          Per-channel refresh status and individual controls.
        </p>
      </header>

      {!loaded ? (
        <div className="rounded-md border border-border bg-surface-2 p-6 text-sm text-text-muted">
          Loading channels…
        </div>
      ) : channels.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-2 p-6 text-sm text-text-muted">
          Watched channels appear here once you pick some.{' '}
          <Link to={watchedChannelsHref} className="text-link underline">
            Set up watched channels
          </Link>
          .
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {channels.map((c) => (
            <ChannelSnapshotCard
              key={c.discordChannelId}
              channel={c}
              gameId={gameId ?? ''}
              refreshVersion={version}
            />
          ))}
        </div>
      )}
    </div>
  );
}
